import { createFileRoute, Link } from "@tanstack/react-router";
import heroCotto from "@/assets/hero-cotto.webp";
import artigiano from "@/assets/artigiano.webp";
import superficiImg from "@/assets/superfici.webp";
import logo from "@/assets/logo.webp";
import prima1 from "@/assets/prima1.webp.asset.json";
import dopo1 from "@/assets/dopo1.webp.asset.json";
import prima2 from "@/assets/prima2.webp.asset.json";
import dopo2 from "@/assets/dopo2.webp.asset.json";
import prima3 from "@/assets/prima3.webp.asset.json";
import dopo3 from "@/assets/dopo3.webp.asset.json";
import libera1 from "@/assets/libera1.webp.asset.json";
import libera3 from "@/assets/libera3.webp.asset.json";
import libera4 from "@/assets/libera4.webp.asset.json";
import libera5 from "@/assets/libera5.webp.asset.json";
import terrazzaCotto from "@/assets/pavimento-cotto-terrazza-lavato-brescia.webp.asset.json";
import porticoCotto from "@/assets/portico-cotto-esterno-trattato-lombardia.webp.asset.json";
import scalaCotto from "@/assets/scala-in-cotto-antico-pulita.webp.asset.json";
import { BeforeAfter } from "@/components/BeforeAfter";
import { PreventivoForm } from "@/components/PreventivoForm";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SocialLinks, FACEBOOK_URL, INSTAGRAM_URL } from "@/components/Social";
import { GOOGLE_MAPS_URL } from "@/lib/site";

const TEL_ANTONIO = "+393397517813";
const TEL_LUCA = "+393517204660";
const EMAIL = "artigianidelcotto@gmail.com";
const ANNO_FONDAZIONE = 1990;
const ANNI_ESPERIENZA = "oltre trent'anni";

const servizi = [
  {
    id: "recupero",
    titolo: "Recupero e lavaggio",
    testo:
      "Lavaggio profondo di mattonelle di nuova posatura o superfici rovinate: rimuoviamo residui di posa, pittura e macchie con detergente acido tamponato a base d'acqua, non nocivo. Dove serve interveniamo con carteggiatura a dischi abrasivi.",
    voci: ["Cotto antico e moderno", "Pietre e marmi", "Seminati, mosaici, graniglie e cementine", "Grès porcellanati"],
  },
  {
    id: "trattamento",
    titolo: "Trattamento delle superfici",
    testo:
      "Su cotto e graniglie applichiamo un fondo protettivo antimacchia, poi la finitura scelta con il cliente: cera per una lucentezza naturale oppure effetto satinato. Sul marmo, detergenti idrorepellenti e cere per riportarne la brillantezza.",
    voci: ["Fondo antimacchia", "Cere lucide o satinate", "Olio di lino e cere in pasta", "Tonalizzazione"],
  },
  {
    id: "manutenzione",
    titolo: "Manutenzione e restyling",
    testo:
      "Con l'uso, gli strati di cera si consumano. Rimuoviamo il vecchio strato con deceranti professionali e ritrattiamo la superficie: il pavimento torna al suo splendore, senza rifacimenti.",
    voci: ["Deceratura completa", "Nuovo trattamento protettivo", "Interventi programmati", "Sopralluogo e preventivo"],
  },
];

const lavori = [
  {
    label: "Lavaggio e recupero di un cortile in cotto",
    before: prima1.url,
    after: dopo1.url,
  },
  {
    label: "Scala e camminamento esterni riportati a nuovo",
    before: prima3.url,
    after: dopo3.url,
  },

  {
    label: "Terrazza in cotto lavata e trattata",
    before: prima2.url,
    after: dopo2.url,
  },
];

const galleria = [
  { src: libera1.url, alt: "Pavimento in cotto antico di una sala con camino, dopo il trattamento" },
  { src: libera3.url, alt: "Cotto fatto a mano in una cucina d'epoca dopo il recupero" },
  { src: libera4.url, alt: "Porfido a coda di pavone lavato e protetto" },
  { src: libera5.url, alt: "Portico di una cascina con pavimento in cotto trattato" },
  {
    src: terrazzaCotto.url,
    alt: "Pavimento in cotto di una terrazza dopo lavaggio e trattamento protettivo in provincia di Brescia",
  },
  {
    src: porticoCotto.url,
    alt: "Portico con pavimento in cotto a spina di pesce lavato e trattato in Lombardia",
  },
  {
    src: scalaCotto.url,
    alt: "Scala in cotto antico fatto a mano dopo pulizia professionale e deceratura",
  },
];

// Video dei lavori (senza confronto prima/dopo).
// Aggiungere qui i file caricati: { src: "/video/nome.mp4", poster: "...", label: "..." }
const video: { src: string; poster?: string; label: string }[] = [];

const province = [
  "Brescia",
  "Bergamo",
  "Milano",
  "Monza e Brianza",
  "Cremona",
  "Mantova",
  "Lodi",
  "Pavia",
  "Como",
  "Lecco",
  "Varese",
  "Sondrio",
];

// Province con una pagina dedicata: i chip diventano link interni.
const pagineProvincia: Record<string, string> = {
  Brescia: "/trattamento-pavimenti-cotto-brescia",
  Bergamo: "/trattamento-pavimenti-cotto-bergamo",
  Milano: "/trattamento-pavimenti-cotto-milano",
};


export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: "Trattamento Pavimenti in Cotto in Lombardia | Artigiani del Cotto" },
      {
        name: "description",
        content:
          "Lavaggio, deceratura e trattamento pavimenti in cotto a Brescia e in Lombardia. Preventivo gratuito e sopralluogo senza impegno: chiama 339 751 7813.",
      },
      { property: "og:title", content: "Trattamento Pavimenti in Cotto in Lombardia | Artigiani del Cotto" },
      {
        property: "og:description",
        content:
          "Lavaggio, deceratura e trattamento pavimenti in cotto a Brescia e in Lombardia. Preventivo gratuito e sopralluogo senza impegno: chiama 339 751 7813.",
      },
      { name: "twitter:title", content: "Trattamento Pavimenti in Cotto in Lombardia | Artigiani del Cotto" },
      {
        name: "twitter:description",
        content:
          "Lavaggio, deceratura e trattamento pavimenti in cotto a Brescia e in Lombardia. Preventivo gratuito e sopralluogo senza impegno: chiama 339 751 7813.",
      },
      { property: "og:url", content: "https://artigianidelcotto.com/" },
      { name: "geo.region", content: "IT-25" },
      { name: "geo.placename", content: "Torbole Casaglia, Brescia, Lombardia" },
    ],

    links: [{ rel: "canonical", href: "https://artigianidelcotto.com/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
              "@id": "https://artigianidelcotto.com/#azienda",
              name: "Artigiani del Cotto",
              alternateName: "Artigiani del Cotto",
              description:
                "Recupero, lavaggio, trattamento protettivo e manutenzione di pavimenti in cotto, pietre, marmi, graniglie e grès porcellanati in tutta la Lombardia.",
              url: "https://artigianidelcotto.com/",
              logo: {
                "@type": "ImageObject",
                url: `https://artigianidelcotto.com${logo}`,
              },
              image: `https://artigianidelcotto.com${heroCotto}`,
              telephone: "+39 339 751 7813",
              email: EMAIL,
              foundingDate: String(ANNO_FONDAZIONE),
              priceRange: "€€",
              currenciesAccepted: "EUR",
              paymentAccepted: "Contanti, Bonifico bancario",
              vatID: "03451350171",
              sameAs: [FACEBOOK_URL, INSTAGRAM_URL, GOOGLE_MAPS_URL],
              hasMap: GOOGLE_MAPS_URL,
              address: {
                "@type": "PostalAddress",
                streetAddress: "Via Piave 7/B",
                postalCode: "25030",
                addressLocality: "Torbole Casaglia",
                addressRegion: "BS",
                addressCountry: "IT",
              },
              geo: {
                "@type": "GeoCoordinates",
                latitude: "45.5167",
                longitude: "10.1167",
              },
              areaServed: {
                "@type": "GeoCircle",
                name: "Lombardia e province limitrofe",
                geoMidpoint: {
                  "@type": "GeoCoordinates",
                  latitude: "45.5167",
                  longitude: "10.1167",
                },
                geoRadius: "100000",
              },
              openingHoursSpecification: [
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                  opens: "08:00",
                  closes: "19:00",
                },
              ],
              knowsAbout: [
                "lavaggio e trattamento pavimenti in cotto",
                "lavaggio e trattamento protettivo di marmi e pietre",
                "lavaggio e trattamento di graniglie e seminati",
                "deceratura e ceratura pavimenti",
              ],
            },
            {
              "@type": "Service",
              "@id": "https://artigianidelcotto.com/#servizio",
              name: "Trattamento e recupero pavimenti in cotto in Lombardia",
              serviceType: "Lavaggio e trattamento protettivo pavimenti",
              description:
                "Lavaggio, deceratura e trattamento protettivo di pavimenti in cotto, marmi, pietre, graniglie e grès porcellanati in tutta la Lombardia.",
              url: "https://artigianidelcotto.com/",
              provider: { "@id": "https://artigianidelcotto.com/#azienda" },
              areaServed: [
                { "@type": "AdministrativeArea", name: "Lombardia" },
                ...province.map((p) => ({ "@type": "City", name: p })),
              ],
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "Servizi di trattamento pavimenti",
                itemListElement: [
                  {
                    "@type": "Offer",
                    itemOffered: {
                      "@type": "Service",
                      name: "Recupero e lavaggio pavimenti",
                      description:
                        "Lavaggio profondo di mattonelle di nuova posatura o superfici rovinate: rimozione residui di posa, pittura e macchie con detergente acido tamponato a base d'acqua.",
                    },
                  },
                  {
                    "@type": "Offer",
                    itemOffered: {
                      "@type": "Service",
                      name: "Trattamento protettivo superfici",
                      description:
                        "Applicazione fondo antimacchia e finitura a cera lucida o satinata su cotto e graniglie; idrorepellenti e cere per marmo e pietre.",
                    },
                  },
                  {
                    "@type": "Offer",
                    itemOffered: {
                      "@type": "Service",
                      name: "Manutenzione e restyling pavimenti",
                      description:
                        "Deceratura completa del vecchio strato e ritrattamento protettivo per riportare il pavimento al suo splendore senza rifacimenti.",
                    },
                  },
                ],
              },
              offers: {
                "@type": "Offer",
                priceCurrency: "EUR",
                availability: "https://schema.org/InStock",
                url: "https://artigianidelcotto.com/#contatti",
                description: "Sopralluogo e preventivo gratuiti e senza impegno.",
              },
            },
            {
              "@type": "WebSite",
              "@id": "https://artigianidelcotto.com/#sito",
              url: "https://artigianidelcotto.com/",
              name: "Artigiani del Cotto",
              publisher: { "@id": "https://artigianidelcotto.com/#azienda" },
            },
            {
              "@type": "FAQPage",
              "@id": "https://artigianidelcotto.com/#faq",
              mainEntity: [
                {
                  "@type": "Question",
                  name: "In quali zone della Lombardia intervenite?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Lavoriamo in tutta la Lombardia: Brescia, Bergamo, Milano, Monza e Brianza, Cremona, Mantova, Lodi, Pavia, Como, Lecco, Varese e Sondrio, e nelle regioni confinanti.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Il preventivo è gratuito?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Sì. Sopralluogo e preventivo sono gratuiti e senza impegno. Bastano anche due foto inviate via WhatsApp per una prima stima.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Ogni quanto va rifatto il trattamento di un pavimento in cotto?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Dipende dall'uso: in una abitazione il ciclo di cera dura in media alcuni anni. Quando si consuma si esegue una deceratura e un nuovo trattamento, senza rifare il pavimento.",
                  },
                },
              ],
            },
          ],
        }),
      },
    ],
  }),
});

function Index() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader home />


      <main id="top">
        <section className="relative">
          <img
            src={heroCotto}
            fetchPriority="high"
            alt="Pavimento in cotto antico lucidato in una casa ristrutturata in Lombardia"
            width={1600}
            height={1067}
            decoding="async"
            className="h-[68vh] min-h-[440px] w-full object-cover"
          />
          <div className="absolute inset-0 bg-linear-to-r from-ink/85 via-ink/55 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="mx-auto w-full max-w-6xl px-5">
              <div className="max-w-xl text-background">
                <p className="eyebrow text-sand">Dal {ANNO_FONDAZIONE} in tutta la Lombardia</p>
                <h1 className="mt-4 text-4xl leading-[1.05] font-semibold sm:text-5xl md:text-6xl">
                  Trattamento e recupero pavimenti in cotto in Lombardia
                </h1>
                <p className="mt-3 max-w-lg text-lg text-background/90 italic">
                  Il vostro pavimento è in buone mani. Le nostre.
                </p>
                <p className="mt-5 max-w-lg text-base text-background/85">
                  Recupero, trattamento e manutenzione di cotto antico e moderno, pietre,
                  marmi, graniglie e grès. {ANNI_ESPERIENZA} di lavoro a mano,
                  superficie per superficie.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href="#contatti"
                    className="rounded-xs bg-copper px-6 py-3 text-sm font-semibold text-accent-foreground transition-transform hover:-translate-y-0.5"
                  >
                    Richiedi un preventivo gratuito
                  </a>
                  <a
                    href="#lavori"
                    className="rounded-xs border border-background/40 px-6 py-3 text-sm font-semibold transition-colors hover:bg-background/10"
                  >
                    Guarda i lavori
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-secondary/50">
          <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-5 py-8 text-center md:grid-cols-4">
            {[
              [`Oltre trent'anni`, `di attività dal ${ANNO_FONDAZIONE}`],
              ["Cotto · Marmo", "pietre, graniglie, grès"],
              ["Tutta la Lombardia", "e regioni limitrofe"],
              ["Sopralluogo", "e preventivo gratuiti"],
            ].map(([big, small]) => (
              <div key={small}>
                <p className="font-display text-lg font-semibold text-sienna">{big}</p>
                <p className="mt-1 text-xs tracking-wide text-muted-foreground uppercase">{small}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="servizi" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-20">
          <p className="eyebrow">Servizi</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Tre interventi, un solo obiettivo: far tornare la superficie come deve essere.
          </h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {servizi.map((s, i) => (
              <article
                key={s.id}
                className="surface-warm flex flex-col rounded-sm border border-border p-7 transition-transform hover:-translate-y-1"
              >
                <span className="font-display text-sm font-semibold text-copper">
                  0{i + 1}
                </span>
                <h3 className="mt-3 text-xl font-semibold">{s.titolo}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.testo}</p>
                <ul className="mt-6 space-y-2 border-t border-border pt-5 text-sm">
                  {s.voci.map((v) => (
                    <li key={v} className="flex gap-2">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-copper" />
                      <span>{v}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <p className="mt-8 text-sm">
            Vuoi far lavare e trattare il cotto di casa, ufficio o negozio?{" "}
            <Link
              to="/pulizia-lavaggio-cotto"
              className="font-semibold text-primary underline-offset-4 hover:underline"
            >
              Scopri la pulizia e il lavaggio del cotto
            </Link>
            . Hai un cortile, una terrazza o una scala all'aperto?{" "}
            <Link
              to="/trattamento-cotto-esterno"
              className="font-semibold text-primary underline-offset-4 hover:underline"
            >
              Vai al trattamento del cotto esterno
            </Link>
            .
          </p>
        </section>


        <section id="lavori" className="scroll-mt-20 bg-secondary/40 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow">Prima e dopo</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
              Trascina il cursore e guarda la differenza.
            </h2>
            <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {lavori.map((l) => (
                <BeforeAfter key={l.after} {...l} />
              ))}
            </div>

            <div className="mt-20">
              <p className="eyebrow">Altri lavori</p>
              <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
                Superfici finite, così come le lasciamo.
              </h2>
              <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {galleria.map((g) => (
                  <img
                    key={g.alt}
                    src={g.src}
                    alt={g.alt}
                    width={750}
                    height={1000}
                    loading="lazy"
                    className="aspect-4/3 w-full rounded-sm object-cover shadow-[var(--shadow-soft)]"
                  />
                ))}
              </div>
            </div>

            {video.length > 0 && (
              <div className="mt-20">
                <p className="eyebrow">Video</p>
                <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
                  Il lavoro in corso.
                </h2>
                <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {video.map((v) => (
                    <figure key={v.src}>
                      <video
                        src={v.src}
                        poster={v.poster}
                        controls
                        playsInline
                        preload="metadata"
                        className="aspect-4/3 w-full rounded-sm bg-muted object-cover"
                      />
                      <figcaption className="mt-3 text-sm text-muted-foreground">{v.label}</figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        <section id="metodo" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-20">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <p className="eyebrow">Chi siamo</p>
              <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
                Artigiani dal {ANNO_FONDAZIONE}, non semplici pulitori.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                Antonio e Luca lavorano il cotto da {ANNI_ESPERIENZA} con prodotti
                professionali certificati, scegliendo per ogni pavimento il ciclo giusto:
                lavaggio, fondo antimacchia, finitura lucida o satinata. Nessuna ricetta
                uguale all'altra, perché nessun pavimento è uguale all'altro.
              </p>
              <ul className="mt-8 space-y-4 text-sm">
                {[
                  "Sopralluogo e preventivo gratuiti, senza impegno.",
                  "Prodotti a base d'acqua, non nocivi, sicuri per la casa.",
                  "Interventi su abitazioni, cascine, negozi e locali storici.",
                  "Manutenzione programmata per non rovinare il trattamento.",
                ].map((t) => (
                  <li key={t} className="flex gap-3 border-b border-border pb-4">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid gap-4">
              <img
                src={artigiano}
                alt="Applicazione della cera protettiva su un pavimento in cotto"
                width={1000}
                height={1250}
                loading="lazy"
                className="h-80 w-full rounded-sm object-cover shadow-[var(--shadow-lift)]"
              />
              <img
                src={superficiImg}
                alt="Marmo, graniglia veneziana e grès porcellanato a confronto"
                width={1000}
                height={713}
                loading="lazy"
                className="h-48 w-full rounded-sm object-cover"
              />
            </div>
          </div>
        </section>

        <section className="gradient-warm py-16 text-background">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow text-sand">Dove lavoriamo</p>
            <h2 className="mt-3 text-2xl font-semibold sm:text-3xl">
              Recupero pavimenti in cotto in tutta la Lombardia.
            </h2>
            <p className="mt-4 max-w-2xl text-background/85">
              Con sede a Torbole Casaglia, in provincia di Brescia, interveniamo in tutte le
              province lombarde e ci spostiamo volentieri in Veneto, Emilia-Romagna, Piemonte
              e Trentino per cantieri di recupero.
            </p>
            <ul className="mt-8 flex flex-wrap gap-2">
              {province.map((p) => {
                const to = pagineProvincia[p];
                return (
                  <li key={p}>
                    {to ? (
                      <Link
                        to={to}
                        className="inline-block rounded-xs border border-background/50 bg-background/10 px-3 py-1.5 text-sm font-medium text-background transition-colors hover:bg-background hover:text-sienna"
                      >
                        Cotto a {p}
                      </Link>
                    ) : (
                      <span className="inline-block rounded-xs border border-background/25 px-3 py-1.5 text-sm text-background/90">
                        {p}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              <Link
                to="/trattamento-cotto-esterno"
                className="group rounded-sm border border-background/25 bg-background/10 p-5 transition-colors hover:bg-background/20"
              >
                <p className="font-display text-lg font-semibold text-background">
                  Trattamento cotto esterno
                </p>
                <p className="mt-2 text-sm text-background/85">
                  Terrazzi, porticati e cortili: pulizia del salnitro, protezione antigelo e
                  finitura idrorepellente.
                </p>
                <span className="mt-3 inline-block text-sm font-semibold text-sand group-hover:underline">
                  Scopri il servizio →
                </span>
              </Link>
              <Link
                to="/come-pulire-il-cotto"
                className="group rounded-sm border border-background/25 bg-background/10 p-5 transition-colors hover:bg-background/20"
              >
                <p className="font-display text-lg font-semibold text-background">
                  Come pulire il cotto
                </p>
                <p className="mt-2 text-sm text-background/85">
                  La guida pratica dei nostri artigiani: prodotti giusti, errori da evitare e
                  manutenzione nel tempo.
                </p>
                <span className="mt-3 inline-block text-sm font-semibold text-sand group-hover:underline">
                  Leggi la guida →
                </span>
              </Link>
            </div>

          </div>
        </section>

        <section id="contatti" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-20">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="eyebrow">Contatti</p>
              <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
                Raccontaci il tuo pavimento.
              </h2>
              <p className="mt-4 text-muted-foreground">
                Una telefonata o un messaggio con due foto bastano per un primo
                preventivo indicativo. Rispondiamo in giornata.
              </p>
              <div className="mt-8 space-y-3">
                <a
                  href={`tel:${TEL_ANTONIO}`}
                  className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary"
                >
                  <span className="font-medium">Antonio</span>
                  <span className="text-primary">+39 339 751 7813</span>
                </a>
                <a
                  href={`tel:${TEL_LUCA}`}
                  className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary"
                >
                  <span className="font-medium">Luca</span>
                  <span className="text-primary">+39 351 720 4660</span>
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary"
                >
                  <span className="font-medium">Email</span>
                  <span className="text-primary">{EMAIL}</span>
                </a>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <a
                  href={`https://wa.me/${TEL_ANTONIO.replace("+", "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex rounded-xs bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-sienna"
                >
                  Scrivi ad Antonio su WhatsApp
                </a>
                <SocialLinks />
              </div>
              <p className="mt-8 text-sm text-muted-foreground">
                Sede operativa: Via Piave 7/B, 25030 Torbole Casaglia (BS).
                Lavoriamo su appuntamento, direttamente presso il cliente.
              </p>
            </div>
            <PreventivoForm />
          </div>
        </section>
      </main>

      <SiteFooter />

    </div>
  );
}
